
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Quote, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface Testimonial {
  id: string;
  name: string;
  role: string;
  quote: string;
}

const testimonials: Testimonial[] = [
  {
    id: "1",
    name: "Kilian",
    role: "Créateur TikTok",
    quote: "ReCut, c'est pour moi l'assurance de créer en toute légalité. En tant que créateur de contenu cinéma, utiliser des images protégées sans risque de bannissement est un luxe inestimable. Les challenges stimulants et le contact humain sincère sur la plateforme m'inspirent à repousser les limites de ma créativité. ReCut, c'est une vraie révolution pour le cinéma français."
  },
  {
    id: "2",
    name: "Sébastien Aubert",
    role: "Fondateur d'Adastra Films",
    quote: "ReCut nous permet de toucher une communauté de créateurs à un tarif accessible, tout en renforçant la sortie de nos films auprès des jeunes publics. Je suis convaincu que c'est un partenaire clé pour toute stratégie de communication audiovisuelle."
  }
];

// Character limit before showing "Read more" button
const QUOTE_CHARACTER_LIMIT = 140;

const TestimonialCard = ({
  testimonial
}: {
  testimonial: Testimonial;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLongQuote = testimonial.quote.length > QUOTE_CHARACTER_LIMIT;
  const displayedQuote = isLongQuote && !expanded ? `${testimonial.quote.substring(0, QUOTE_CHARACTER_LIMIT)}...` : testimonial.quote;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <Card className="h-full bg-background/70 backdrop-blur-md border border-primary/20 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
        <div className="h-2 bg-primary/60"></div>
        <CardContent className="p-8 sm:p-10">
          <Quote className="h-12 w-12 mb-6 text-primary/60" />
          
          <p className="text-xl mb-8 leading-relaxed font-normal">
            "{displayedQuote}"
          </p>
          
          {isLongQuote && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setExpanded(!expanded)} 
              className="mb-8 p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent"
            >
              {expanded ? (
                <span className="flex items-center gap-1">Réduire <ChevronUp className="h-4 w-4" /></span>
              ) : (
                <span className="flex items-center gap-1">Lire plus <ChevronDown className="h-4 w-4" /></span>
              )}
            </Button>
          )}
          
          <div className="flex flex-col">
            <p className="text-xl font-bold">{testimonial.name}</p>
            <p className="text-base text-muted-foreground mt-1">{testimonial.role}</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const TestimonialsSection = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent opacity-70 pointer-events-none"></div>
      <div className="absolute top-1/4 left-0 w-60 h-60 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-0 w-60 h-60 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ils créent déjà avec <span className="text-primary">nous</span> !
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Découvre comment ReCut booste la carrière des créateurs en partenariat direct avec les ayants droit. Grâce à notre accompagnement personnalisé et à notre communauté dynamique, transforme ton hobby en profession.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
